import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { sendAnnouncement } from '../../services/emailService';
import { sendAnnouncementViaConstantContact } from '../../services/constantContactService';
import { sendAnnouncementNotification } from '../../services/notificationService';

const AnnouncementForm = ({ onClose, onSuccess }) => {
  const { hasRole } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    actionUrl: '',
    includeActionButton: false,
    emailService: 'emailjs' // 'emailjs' or 'constantcontact'
  });
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);

  const isAdmin = hasRole('admin');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;

    setLoading(true);
    try {
      let result;

      // Send email announcement
      if (formData.emailService === 'constantcontact') {
        result = await sendAnnouncementViaConstantContact(
          formData.title,
          formData.message,
          formData.includeActionButton ? formData.actionUrl : null
        );
      } else {
        result = await sendAnnouncement(
          formData.title,
          formData.message,
          formData.includeActionButton ? formData.actionUrl : null
        );
      }

      // Also send in-app notification
      await sendAnnouncementNotification(
        formData.title,
        formData.message,
        formData.includeActionButton ? formData.actionUrl : null
      );

      const successMessage = formData.emailService === 'constantcontact'
        ? 'Announcement sent successfully via Constant Contact and in-app notifications!'
        : `Announcement sent successfully to ${result.successCount} members via email and in-app notifications!`;

      alert(successMessage);
      onSuccess && onSuccess(result);
      onClose && onClose();
    } catch (error) {
      console.error('Error sending announcement:', error);
      alert('Error sending announcement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to send announcements.</p>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Send Announcement</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!preview ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Announcement Title *
              </label>
              <input
                type="text"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter announcement title"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message *
              </label>
              <textarea
                name="message"
                required
                rows={6}
                value={formData.message}
                onChange={handleChange}
                placeholder="Enter your announcement message..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Service
              </label>
              <select
                name="emailService"
                value={formData.emailService}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
              >
                <option value="emailjs">EmailJS (Development)</option>
                <option value="constantcontact">Constant Contact (Production)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.emailService === 'constantcontact'
                  ? 'Professional email marketing with analytics and deliverability'
                  : 'Simple email service for development and testing'
                }
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="includeActionButton"
                name="includeActionButton"
                checked={formData.includeActionButton}
                onChange={handleChange}
                className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
              />
              <label htmlFor="includeActionButton" className="text-sm font-medium text-gray-700">
                Include action button
              </label>
            </div>

            {formData.includeActionButton && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Action URL
                </label>
                <input
                  type="url"
                  name="actionUrl"
                  value={formData.actionUrl}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                />
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">Important</h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    This announcement will be sent to all approved members via email. Please review carefully before sending.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setPreview(true)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition duration-200"
              >
                Preview
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-apn-blue text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Announcement'
                )}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Preview</h3>
            
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="bg-apn-blue text-white p-4 rounded-t-lg">
                <h1 className="text-xl font-bold">Accelerated Professionals Network</h1>
              </div>
              <div className="bg-white p-6 rounded-b-lg">
                <h2 className="text-xl font-bold text-gray-900 mb-4">{formData.title}</h2>
                <div className="text-gray-700 whitespace-pre-wrap mb-4">{formData.message}</div>
                {formData.includeActionButton && formData.actionUrl && (
                  <div className="text-center">
                    <a
                      href={formData.actionUrl}
                      className="inline-block bg-apn-blue text-white py-3 px-6 rounded-lg text-decoration-none"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn More
                    </a>
                  </div>
                )}
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setPreview(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition duration-200"
              >
                Back to Edit
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-apn-blue text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send Announcement'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementForm;
